import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { map, distinctUntilChanged } from 'rxjs/operators';
import { RouteService } from 'src/app/core/services/route.service';
import { ToggleNavigation } from 'src/app/core/state/_actions/table.actions';
import { Item, Navigation } from 'src/app/core/state/_models/table.models';
import { APP_ROUTE_PATTERN } from '../../constants';
import { CommonService } from 'src/app/core/services/common.service';

@Component({
  selector: 'app-new-side-navigation',
  templateUrl: './new-side-navigation.component.html',
  styleUrls: ['./new-side-navigation.component.scss'],
})
export class NewSideNavigationComponent implements OnInit, OnChanges {
  @Input('navigations') navigations: Array<Navigation> = [];
  @Input() externalCollapse = false;
  @Output() loadDefaultTable = new EventEmitter<any>();
  @Output() panelExpanded = new EventEmitter<void>();

  activeLink: string | null = null;
  expandedIndex: number | null = null;
  isFromLayout: boolean = false;

  constructor(
    private readonly route: RouteService,
    private readonly store: Store,
    private readonly commonService: CommonService
  ) {}

  ngOnInit(): void {
    if (this.navigations.length) {
      this.isFromLayout = this.navigations.every((nav: any) => nav.hasOwnProperty('LayoutKey'));
    }
    this.route.change().pipe(
      map((e: any) => e?.url || e?.urlAfterRedirects || this.route.currentUrl),
      distinctUntilChanged()
    ).subscribe((url) => {
      this.updateActiveLink(url);
      this.expandPanelContainingActiveLink(url);
    });
    this.updateActiveLink(this.route.currentUrl);
    this.expandPanelContainingActiveLink();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['navigations']?.currentValue?.length) {
      const prev = changes['navigations'].previousValue;
      const wasEmpty = !prev || prev.length === 0;
      this.navigations = changes['navigations'].currentValue;
      this.isFromLayout = this.navigations.every((nav: any) => nav.hasOwnProperty('LayoutKey'));
      this.updateActiveLink(this.route.currentUrl);
      if (wasEmpty) {
        this.expandPanelContainingActiveLink(this.route.currentUrl);
      }
    }
    if (changes['externalCollapse']?.currentValue === true && this.expandedIndex != null) {
      const prevNav = this.navigations[this.expandedIndex];
      if (prevNav) {
        this.store.dispatch(new ToggleNavigation(this.route.getApplicationKey(), prevNav.Order, false));
      }
      this.expandedIndex = null;
    }
  }

  togglePanel(index: number): void {
    if (this.expandedIndex === index) {
      this.expandedIndex = null;
      const nav = this.navigations[index];
      if (nav) {
        this.store.dispatch(new ToggleNavigation(this.route.getApplicationKey(), nav.Order, false));
      }
    } else {
      const prevNav = this.expandedIndex != null ? this.navigations[this.expandedIndex] : null;
      if (prevNav) {
        this.store.dispatch(new ToggleNavigation(this.route.getApplicationKey(), prevNav.Order, false));
      }
      this.expandedIndex = index;
      const nav = this.navigations[index];
      if (nav) {
        this.store.dispatch(new ToggleNavigation(this.route.getApplicationKey(), nav.Order, true));
      }
      this.panelExpanded.emit();
    }
  }

  togglePanelWithFocus(index: number): void {
    const wasExpanded = this.expandedIndex === index;
    this.togglePanel(index);
    if (wasExpanded) {
      this.focusToggleButton(index);
    } else if (this.expandedIndex === index) {
      this.focusFirstSubItem(index);
    }
  }

  focusToggleButton(navIndex: number): void {
    setTimeout(() => {
      const nav = this.navigations[navIndex];
      const toggleId = 'toggle-' + (nav?.LevelIds?.TableId ?? nav?.Order ?? navIndex);
      const toggle = document.getElementById(toggleId);
      toggle?.focus();
    }, 0);
  }

  focusFirstSubItem(navIndex: number): void {
    setTimeout(() => {
      if (this.expandedIndex !== navIndex) return;
      const nav = this.navigations[navIndex];
      const panelId = 'panel-' + (nav?.LevelIds?.TableId ?? nav?.Order ?? navIndex);
      const panel = document.getElementById(panelId);
      const firstFocusable = panel?.querySelector<HTMLElement>('a.new-sub-link, button.new-sub-link');
      firstFocusable?.focus();
    }, 0);
  }

  expandPanelContainingActiveLink(url?: string): void {
    const currentUrl = url ?? this.route.currentUrl;
    for (let i = 0; i < this.navigations.length; i++) {
      const nav = this.navigations[i];
      const hasActive = nav?.Items?.some(
        (item) => this.getRouterLink(item) === currentUrl || currentUrl.startsWith(this.getRouterLink(item))
      );
      if (hasActive) {
        this.expandedIndex = i;
        this.panelExpanded.emit();
        return;
      }
    }
  }

  getRouterLink = (item: Item): string => {
    if (!item?.Href) return '/apps';

    const appRegex = APP_ROUTE_PATTERN;
    let link: Array<string> = [];

    if (this.fromSameApp(item)) {
      const originOrHost = item.Href.includes(window.location.origin)
        ? window.location.origin
        : window.location.host;
      return item.Href.replace(originOrHost, '');
    }

    if (this.isRouterLink(item.Href)) {
      link = this.getFilteredLink(item.Href);
    } else if (item.Href.includes('http')) {
      try {
        const url = new URL(item.Href);
        if (appRegex.test(url.pathname)) return url.pathname;
      } catch {
        return '/apps';
      }
    } else if (appRegex.test(item.Href)) {
      return item.Href;
    } else {
      return '/apps';
    }

    if (link?.[1] === 'viewdatatable' && item.Title.startsWith('View')) {
      return [
        '/apps', link[2], 'tables', link[3], link[0] + 's', 'datatable', 'view', link[4],
      ].join('/');
    }

    if (String(item.Title).startsWith('Add')) {
      return [
        '/apps', link[2], 'tables', link[3], link[0] + 's', 'datatable', 'view', link[4],
        'form', link[5], 'add',
      ].join('/');
    }

    return [
      '/apps', link[1], 'tables', link[2],
      (link[0] === 'tablesettings' ? 'setting' : link[0]) + 's',
    ].join('/');
  };

  fromSameApp(item: any): boolean {
    return item.Href.includes(window.location.origin) || item.Href.includes(window.location.host);
  }

  openLinkInNewTab(item: any): void {
    let link = item.Href;
    if (!/^https?:\/\//i.test(link)) link = 'http://' + link;
    window.open(link, '_blank');
  }

  isRouterLink(link: any): boolean {
    return /^\/[^\/\s]+/.test(link);
  }

  openLink(item: any): void {
    let url = item.Href;
    if (!/^https?:\/\//i.test(url)) url = 'http://' + url;
    if (!this.isRouterLink(url)) {
      const app_regex = APP_ROUTE_PATTERN;
      const newUrl = new URL(url);
      if (app_regex.test(newUrl.pathname)) {
        this.route.navigate([newUrl.pathname]);
        return;
      }
    }
    window.location.href = url;
  }

  getFilteredLink(link: any): string[] {
    const array = link.split('/');
    const unwantedPattern = /^[#!]+$/;
    return array.filter((item: any) => !unwantedPattern.test(item)).filter((item: any) => !!item);
  }

  getNavigationHref(navigation: any): string {
    return `#TableId-${navigation?.LevelIds?.TableId || navigation.Order}`;
  }

  /**
   * Whether to show the left rectangle indicator for an expanded nav item.
   * Excluded for "Audit Log" and "Notification Audit Log".
   * Shown for "Managers" when it is the last item (table ends there).
   */
  shouldShowExpandIndicator(navigation: Navigation, navIdx: number): boolean {
    if (this.expandedIndex !== navIdx) return false;
    const title = navigation?.Title?.trim() || '';
    if (title === 'Audit Log' || title === 'Notification Audit Log') return false;
    return true;
  }

  updateActiveLink(currentUrl: string): void {
    const routeExists = this.navigations.some((nav) =>
      nav.Items?.some((i) => this.getRouterLink(i) === currentUrl)
    );
    if (routeExists) {
      this.activeLink = currentUrl;
      return;
    }
    const matchingItem = this.navigations
      .map((nav) => nav.Items || [])
      .reduce((acc, items) => acc.concat(items), [])
      .filter((i) => currentUrl.startsWith(this.getRouterLink(i)))
      .sort((a, b) => this.getRouterLink(b).length - this.getRouterLink(a).length)[0];
    this.activeLink = matchingItem ? this.getRouterLink(matchingItem) : null;
  }
}
